import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { useStore } from "react-redux";
import { toast } from "react-toastify";
import { makeStyles, type Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Alert from "@material-ui/lab/Alert";
import { SwapHoriz, ArrowForward } from "@material-ui/icons";
import type { Transaction } from "src/interfaces";
import type { MappedOption } from "src/apiService/modules/transactions";
import {
  balanceTransferMatchSelector,
  transactionByIdSelector,
} from "src/store/transaction/selector";
import NumberField from "src/components/NumberField";
import LoadableButton from "src/components/LoadableButton";
import SalePlatformField from "src/components/SalePlatformField";
import Tooltip from "src/components/Tooltip";
import { DateInput } from "./DateInput";
import { Row } from "./Row";
import {
  TransfersAccountId,
  TransfersAccountName,
} from "src/utils/general-ledger-accounts";
import type { BalanceTransferCard, BalanceTransferState } from "./types";

function useDebouncedRun<T>(fn: (value: T) => void, delayMs: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRunRef = useRef<T | null>(null);

  const run = (value: T) => {
    if (lastRunRef.current === value) return;
    lastRunRef.current = value;
    fnRef.current(value);
  };

  const schedule = (value: T) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      run(value);
    }, delayMs);
  };

  const runNow = (value: T) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    run(value);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return { schedule, runNow };
}

const useBalanceTransferStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    "& > * + *": { marginTop: theme.spacing(2) },
  },
  cardsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  card: {
    flex: 1,
    minWidth: 0,
  },
  cardMatched: {
    borderColor: theme.palette.success.main,
    borderWidth: 2,
    borderStyle: "solid",
  },
  cardTitle: {
    textTransform: "uppercase" as const,
    color: theme.palette.primary.main,
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: theme.typography.subtitle2.fontWeight,
    marginBottom: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  chipMatched: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    textTransform: "none",
  },
  changeTypeButton: {
    marginTop: theme.spacing(1),
    alignSelf: "flex-start",
  },
  dialogActions: {
    paddingTop: theme.spacing(1),
  },
  swapZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 48,
  },
  arrowIcon: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      transform: "rotate(90deg)",
    },
  },
}));

interface TransferSideCardProps {
  side: "from" | "to";
  card: BalanceTransferCard;
  onChange: (next: BalanceTransferCard) => void;
  isMatched: boolean;
  loading: boolean;
  showValidationErrors: boolean;
  onClearValidationErrors: () => void;
  classes: ReturnType<typeof useBalanceTransferStyles>;
}

function TransferSideCard({
  side,
  card,
  onChange,
  isMatched,
  loading,
  showValidationErrors,
  onClearValidationErrors,
  classes,
}: TransferSideCardProps) {
  return (
    <Card
      className={clsx(classes.card, isMatched && classes.cardMatched)}
      variant="outlined"
    >
      <CardContent>
        <div className={classes.cardTitle}>
          <span>{side === "from" ? "From" : "To"}</span>
          {isMatched && (
            <Chip
              label="Matched"
              size="small"
              className={classes.chipMatched}
            />
          )}
        </div>
        <Row text="The card or account used for this side of the transfer.">
          <SalePlatformField
            type="all"
            fullWidth
            disabled={loading}
            label="Bank Account"
            required
            value={{
              id: card.bank_account_id,
              name: card.integration_name,
            }}
            onChange={(v) => {
              onClearValidationErrors();
              onChange({
                ...card,
                bank_account_id: v?.id,
                integration_name: v?.name,
                id: undefined,
                match: undefined,
              });
            }}
            error={showValidationErrors && !card.bank_account_id}
            helperText={
              showValidationErrors && !card.bank_account_id
                ? "Required"
                : undefined
            }
            margin="dense"
            variant="outlined"
          />
        </Row>
        <Row text="">
          <DateInput
            value={card.date}
            loading={loading}
            placeholder=" "
            required
            error={showValidationErrors && !card.date}
            helperText={
              showValidationErrors && !card.date ? "Required" : undefined
            }
            onChange={(e) => {
              onClearValidationErrors();
              onChange({
                ...card,
                date: e.target.value as Date,
                id: undefined,
                match: undefined,
              });
            }}
          />
        </Row>
        <Row text="Optional note for this side of the transfer">
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            label="Description"
            value={card.description ?? ""}
            disabled={loading}
            onChange={(e) => onChange({ ...card, description: e.target.value })}
          />
        </Row>
      </CardContent>
    </Card>
  );
}

export interface BalanceTransferFormProps {
  data: Partial<Transaction> | undefined;
  loading: boolean;
  onSave: (
    transaction: Transaction,
    opts?: { next?: boolean; mapped?: MappedOption },
    newImageFile?: File
  ) => void;
  onSwitchToDefaultForm: () => void;
  setDialog: (d: any) => void;
  newImageFile?: File;
}

function BalanceTransferForm({
  data,
  loading,
  onSave,
  onSwitchToDefaultForm,
  setDialog,
  newImageFile,
}: BalanceTransferFormProps) {
  const classes = useBalanceTransferStyles();
  const isUpdate = !!data?.id;

  const [amount, setAmount] = useState<number>(() => {
    if (data?.amount != null) return Math.abs(data.amount);
    return 0;
  });

  const store = useStore();
  const [transferState, setTransferState] = useState<BalanceTransferState>(
    () => {
      if (!data?.id) return { root: {}, mapped: {}, rootOnLeft: true };
      const rootIsFrom = (data.amount ?? 0) > 0;
      const root: BalanceTransferCard = {
        bank_account_id: data.bank_account_id,
        integration_name: data.integration_name,
        date: data.date,
        description: data.description ?? "",
      };
      let mapped: BalanceTransferCard = {};

      if (data.mapped) {
        const mappedTx = transactionByIdSelector(store.getState(), data.mapped);
        if (mappedTx) {
          mapped = {
            bank_account_id: mappedTx.bank_account_id,
            integration_name: mappedTx.integration_name,
            date: mappedTx.date,
            description: mappedTx.description ?? "",
            id: data.mapped,
          };
        }
      }
      return { root, mapped, rootOnLeft: rootIsFrom };
    }
  );

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const { root: rootCard, mapped: mappedCard, rootOnLeft } = transferState;
  const leftCard = rootOnLeft ? rootCard : mappedCard;
  const rightCard = rootOnLeft ? mappedCard : rootCard;

  const runMatch = (overrides?: {
    amount?: number;
    root?: Partial<BalanceTransferCard>;
    mapped?: Partial<BalanceTransferCard>;
  }) => {
    const amt = Math.abs(overrides?.amount ?? amount);
    const root = overrides?.root
      ? { ...rootCard, ...overrides.root }
      : rootCard;

    if (amt <= 0) {
      setTransferState((prev) => ({
        ...prev,
        mapped:
          prev.mapped.id !== undefined || prev.mapped.match
            ? { ...prev.mapped, id: undefined, match: undefined }
            : prev.mapped,
      }));
      return;
    }

    if (!root.bank_account_id || !root.date) {
      setTransferState((prev) =>
        prev.mapped.id !== undefined || prev.mapped.match
          ? {
              ...prev,
              mapped: { ...prev.mapped, id: undefined, match: undefined },
            }
          : prev
      );
      return;
    }

    const searchAmount = rootOnLeft ? amt : -amt;
    const match = balanceTransferMatchSelector(store.getState(), {
      bank_account_id: root.bank_account_id,
      date: root.date,
      amount: searchAmount,
    });
    if (match) {
      setTransferState((prev) => ({
        ...prev,
        mapped: {
          bank_account_id: match.bank_account_id,
          integration_name: match.integration_name,
          date: match.date,
          description: match.description ?? "",
          id: match.id,
          match: true,
        },
      }));
    } else {
      setTransferState((prev) =>
        prev.mapped.id !== undefined || prev.mapped.match
          ? {
              ...prev,
              mapped: { ...prev.mapped, id: undefined, match: undefined },
            }
          : prev
      );
    }
  };

  useEffect(() => {
    // We only need this to run it at the start
    if (isUpdate && data.mapped) return;
    runMatch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { schedule: scheduleAmountMatch, runNow: runAmountMatchNow } =
    useDebouncedRun((amt: number) => runMatch({ amount: amt }), 400);

  const handleAmountBlur = () => runAmountMatchNow(amount);

  const cardHasAnyValue = (card: BalanceTransferCard) =>
    !!(card.bank_account_id || card.date || (card.description ?? "").trim());
  const leftCardShowErrors = showValidationErrors && cardHasAnyValue(leftCard);
  const rightCardShowErrors =
    showValidationErrors && cardHasAnyValue(rightCard);

  const handleSave = (next?: boolean) => {
    const hasRoot = !!(rootCard.bank_account_id && rootCard.date);
    const hasMapped = !!(mappedCard.bank_account_id && mappedCard.date);
    const hasEither = hasRoot || hasMapped;

    if (!hasEither) {
      toast.error(
        "Fill in at least one side (From or To) with Account and Date."
      );
      return;
    }
    if (amount <= 0) {
      setShowValidationErrors(true);
      toast.error("Enter a valid amount.");
      return;
    }
    setShowValidationErrors(false);

    const rootAmount = rootOnLeft ? +Math.abs(amount) : -Math.abs(amount);
    const rootPayload: Transaction = {
      id: data?.id,
      gl_account_id: TransfersAccountId,
      account: TransfersAccountName,
      amount: rootAmount,
      date: rootCard.date,
      description: rootCard.description ?? "",
      bank_account_id: rootCard.bank_account_id,
      integration_name: rootCard.integration_name,
    } as Transaction;

    if (hasRoot && hasMapped) {
      const mappedDate = mappedCard.date ?? new Date();
      const mappedOpt: { mapped: MappedOption } = mappedCard.id
        ? {
            mapped: {
              id: mappedCard.id,
              date: mappedDate,
              description: mappedCard.description ?? "",
            },
          }
        : {
            mapped: {
              date: mappedDate,
              bank_account_id: mappedCard.bank_account_id!,
              integration_name: mappedCard.integration_name ?? "",
              description: mappedCard.description ?? "",
            },
          };
      onSave(rootPayload, { next: !!next, ...mappedOpt }, newImageFile);
    } else {
      onSave(rootPayload, { next: !!next }, newImageFile);
    }
  };

  const swap = () => {
    setTransferState((prev) => ({
      ...prev,
      rootOnLeft: !prev.rootOnLeft,
    }));
  };

  const onlyDescriptionChange = (
    prev: BalanceTransferCard,
    next: BalanceTransferCard
  ) =>
    prev.bank_account_id === next.bank_account_id &&
    prev.integration_name === next.integration_name &&
    prev.date === next.date;

  const handleCardChange = (
    side: "left" | "right",
    next: BalanceTransferCard
  ) => {
    const rootEmpty = !rootCard.bank_account_id && !rootCard.date;
    if (rootEmpty) {
      setTransferState({
        root: next,
        mapped: {},
        rootOnLeft: side === "left",
      });
      runMatch({ root: next });
      return;
    }
    if (side === "left") {
      if (rootOnLeft) {
        setTransferState((prev) => ({ ...prev, root: next }));
        if (!onlyDescriptionChange(rootCard, next)) runMatch({ root: next });
      } else {
        setTransferState((prev) => ({ ...prev, mapped: next }));
      }
    } else {
      if (rootOnLeft) {
        setTransferState((prev) => ({ ...prev, mapped: next }));
      } else {
        setTransferState((prev) => ({ ...prev, root: next }));
        if (!onlyDescriptionChange(rootCard, next)) runMatch({ root: next });
      }
    }
  };

  return (
    <div className={classes.root}>
      {(rootCard.match || mappedCard.match) && (
        <Alert severity="success">
          A matching transaction was found and prefilled.
        </Alert>
      )}
      <Row text="The transfer amount. This will be applied as opposite values to each side.">
        <NumberField
          autoFocus
          fullWidth
          variant="outlined"
          margin="dense"
          label="Amount"
          required
          disabled={loading}
          value={amount}
          onChange={(e) => {
            const v = e.target.value != null ? Number(e.target.value) : 0;
            const nextAmount = v >= 0 ? v : 0;
            setAmount(nextAmount);
            scheduleAmountMatch(nextAmount);
          }}
          onFocus={(e) => e.target.select()}
          onBlur={handleAmountBlur}
          error={showValidationErrors && amount <= 0}
          helperText={
            showValidationErrors && amount <= 0
              ? "Enter a valid amount."
              : undefined
          }
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          fixed={2}
        />
      </Row>
      <div className={classes.cardsContainer}>
        <TransferSideCard
          side="from"
          card={leftCard}
          onChange={(next) => handleCardChange("left", next)}
          isMatched={!!leftCard.match}
          loading={loading}
          showValidationErrors={leftCardShowErrors}
          onClearValidationErrors={() => setShowValidationErrors(false)}
          classes={classes}
        />
        <div className={classes.swapZone}>
          <ArrowForward className={classes.arrowIcon} />
          <Tooltip title="Swap the From and To accounts, dates, and descriptions.">
            <span>
              <IconButton
                onClick={swap}
                disabled={loading}
                aria-label="Swap"
                tabIndex={-1}
              >
                <SwapHoriz />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <TransferSideCard
          side="to"
          card={rightCard}
          onChange={(next) => handleCardChange("right", next)}
          isMatched={!!rightCard.match}
          loading={loading}
          showValidationErrors={rightCardShowErrors}
          onClearValidationErrors={() => setShowValidationErrors(false)}
          classes={classes}
        />
      </div>
      <Button
        color="primary"
        onClick={onSwitchToDefaultForm}
        className={classes.changeTypeButton}
        disabled={loading}
        tabIndex={-1}
      >
        Change to a different transaction type
      </Button>
      <DialogActions className={classes.dialogActions}>
        {!isUpdate && (
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={() => handleSave(true)}
          >
            Add and Next
          </Button>
        )}
        <LoadableButton
          variant="contained"
          color="primary"
          loading={loading}
          onClick={() => handleSave(false)}
        >
          {isUpdate ? "Update" : "Add"}
        </LoadableButton>
      </DialogActions>
    </div>
  );
}

export default BalanceTransferForm;
